import { useEffect, useRef, useState } from "react";

/**
 * Full-width auto-play (muted) background video strip designed to sit just
 * above the global footer. Top edge bleeds into the dark page background via
 * a gradient so it never looks like a hard cut.
 *
 * Props:
 *   src       – video URL (mp4 recommended)
 *   poster    – optional poster image while metadata loads
 *   height    – Tailwind h-* classes (default: aspect 21/9 desktop, 16/9 mobile)
 *   pageBg    – hex color of the page bg (for the top bleed). Default #020817
 *   testid    – data-testid suffix for tests
 */
export default function BackgroundVideoStrip({
  src,
  poster,
  pageBg = "#020817",
  testid = "bg-video-strip",
  heightClass = "aspect-[16/9] md:aspect-[21/9]",
}) {
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Lazy-mount when scrolled near — saves bandwidth on long pages.
  useEffect(() => {
    const el = videoRef.current?.parentElement;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden -mb-px"
      style={{ backgroundColor: pageBg }}
      data-testid={testid}
    >
      {/* Top fade — bleeds the video into the page background */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-32 md:h-48"
        style={{
          background: `linear-gradient(to bottom, ${pageBg} 0%, ${pageBg}cc 35%, ${pageBg}66 65%, transparent 100%)`,
        }}
      />
      {/* Bottom fade — small one so it connects cleanly to footer */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 md:h-20"
        style={{
          background: `linear-gradient(to top, ${pageBg} 0%, transparent 100%)`,
        }}
      />
      <div className={`relative w-full ${heightClass}`}>
        <video
          ref={videoRef}
          src={visible ? src : undefined}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
      </div>
    </section>
  );
}
